import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, DatePicker, Divider, Form, Input, Modal, Popconfirm, Row, Select, Space, Table, Tag, Tooltip, Upload } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusCircleOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import { toast } from "react-toastify";
import { deleteApi, getApi, postApi, putApi } from "utils/apiCall";
import { getPreSignedUrl } from "utils/MyMUtils";
import { UPLOAD_RULES, uploadHint, uploadProps as ruleUploadProps } from "utils/uploadRules";

const { Option } = Select;

const DATE_FORMAT = "DD-MM-YYYY";
const STATUS = {
  valid: { color: "green", text: "Valid" },
  expiring_soon: { color: "orange", text: "Expiring Soon" },
  expired: { color: "red", text: "Expired" },
  unknown: { color: "default", text: "-" },
};

const listOf = ({ error, result }) => (!error && result?.success !== false && Array.isArray(result?.result) ? result.result : []);

// Admin > Certificates: every certificate on one screen (ISO, CM3, Labour
// Hire and names the client adds). Certificate name, expiry date and file are
// required; the name (free text) and states are optional (no states = all states).
const CommonCertificates = () => {
  const [form] = Form.useForm();
  const [types, setTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState({ visible: false, record: null });
  const [file, setFile] = useState(null); // { name, data } of a newly chosen file
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState("");

  // Name starts as the chosen certificate type; once changed by hand it is kept
  const autoName = useRef(null); // the Name last filled in automatically
  const pickType = (typeId, typeName) => {
    const name = ((typeName !== undefined ? typeName : (types.find((t) => t.id === typeId) || {}).type) || "").slice(0, 45);
    const current = form.getFieldValue("name");
    const changedByHand = current && current !== autoName.current;
    form.setFieldsValue({ type_id: typeId, ...(changedByHand ? {} : { name }) });
    if (!changedByHand) autoName.current = name;
  };
  const [addingType, setAddingType] = useState(false);

  const loadTypes = useCallback(async () => setTypes(listOf(await getApi("/certificate_types"))), []);
  const loadCertificates = useCallback(async () => {
    setLoading(true);
    setCertificates(listOf(await getApi("/certificates")));
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTypes();
    (async () => setStates(listOf(await getApi("/get_all_statelist"))))();
  }, [loadTypes]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const openModal = (record = null) => {
    setFile(null);
    setNewType("");
    setModal({ visible: true, record });
    autoName.current = record ? (record.type || "").slice(0, 45) : null;
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        type_id: record.type_id,
        name: record.name || undefined,
        state_id: record.all_states ? [] : record.state_ids,
        expiry_date: record.expiry_date ? moment(record.expiry_date, "YYYY-MM-DD") : undefined,
      });
    }
  };
  const closeModal = () => setModal({ visible: false, record: null });

  const addType = async () => {
    const type = newType.trim();
    if (!type) return toast.error("Please enter the certificate type.");
    setAddingType(true);
    const { error, result } = await postApi("/certificate_type", { type });
    setAddingType(false);
    if (!error && result?.success) {
      toast.success("Certificate name added successfully.");
      setNewType("");
      await loadTypes();
      pickType(result.result.id, result.result.type);
    } else if (result?.result?.id) {
      // already exists: just pick it
      toast.info(result.message);
      pickType(result.result.id, result.result.type);
    } else {
      toast.error(result?.message || "The certificate type could not be added. Please try again.");
    }
  };

  // the file goes to storage first; its link is saved with the certificate.
  // ISO, CM3 and Labour Hire keep their storage folders; other types use the default one.
  const STORAGE_FOLDER = { 3: "ISO-certificate", 4: "CM3-certificate", 5: "labour" };
  const uploadFile = async (values) => {
    const { error, result } = await postApi("/upload_certificate", {
      certificate_type: STORAGE_FOLDER[values.type_id] || "certificate",
      filename: file.name.replace(/\.[^.]+$/, ""),
      file: file.data,
    });
    return !error && result?.result ? result.result : null;
  };

  const save = async (values) => {
    const editing = modal.record;
    if (!editing && !file) return toast.error("Please upload the certificate file.");
    setSaving(true);
    let pdf_link;
    if (file) {
      pdf_link = await uploadFile(values);
      if (!pdf_link) {
        setSaving(false);
        return toast.error("The file could not be uploaded. Please try again.");
      }
    }
    const body = {
      type_id: values.type_id,
      name: values.name || "",
      state_id: values.state_id || [],
      expiry_date: values.expiry_date.format("YYYY-MM-DD"),
      ...(pdf_link ? { pdf_link } : {}),
    };
    const { error, result } = editing ? await putApi(`/certificate/${editing.id}`, body) : await postApi("/certificate", body);
    setSaving(false);
    if (!error && result?.success) {
      toast.success(editing ? "Certificate updated successfully." : "Certificate added successfully.");
      closeModal();
      loadCertificates();
    } else {
      toast.error(result?.message || "The certificate could not be saved. Please try again.");
    }
  };

  const remove = async (record) => {
    const { error, result } = await deleteApi(`/certificate/${record.id}`);
    if (!error && result?.success) {
      toast.success("Certificate deleted successfully.");
      loadCertificates();
    } else {
      toast.error(result?.message || "The certificate could not be deleted. Please try again.");
    }
  };

  const uploadProps = {
    ...ruleUploadProps(UPLOAD_RULES.PDF_OR_IMAGE),
    maxCount: 1,
    onChange: ({ fileList }) => {
      const chosen = fileList.length ? fileList[fileList.length - 1].originFileObj : null;
      if (!chosen) {
        setFile(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => setFile({ name: chosen.name, data: e.target.result });
      reader.readAsDataURL(chosen);
    },
    onRemove: () => setFile(null),
  };

  const columns = [
    { title: "Certificate Type", dataIndex: "type", key: "type", sorter: (a, b) => String(a.type).localeCompare(String(b.type)) },
    { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "-" },
    { title: "State", key: "states", render: (_, r) => (r.all_states ? "All States" : r.state_names || "-") },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      sorter: (a, b) => String(a.expiry_date).localeCompare(String(b.expiry_date)),
      render: (v) => (v ? moment(v, "YYYY-MM-DD").format(DATE_FORMAT) : "-"),
    },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={(STATUS[v] || STATUS.unknown).color}>{(STATUS[v] || STATUS.unknown).text}</Tag> },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button icon={<EyeOutlined />} aria-label="View" disabled={!record.pdf_link} onClick={() => getPreSignedUrl(record.pdf_link)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} aria-label="Edit" onClick={() => openModal(record)} />
          </Tooltip>
          <Popconfirm title="Are you sure you want to delete this certificate?" okText="Delete" okType="danger" onConfirm={() => remove(record)}>
            <Tooltip title="Delete">
              <Button danger icon={<DeleteOutlined />} aria-label="Delete" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <Button type="primary" shape="round" icon={<PlusCircleOutlined />} onClick={() => openModal()}>
          Add Certificate
        </Button>
      </div>

      <Table rowKey="id" columns={columns} dataSource={certificates} loading={loading} locale={{ emptyText: "No certificates yet." }} />

      <Modal title={modal.record ? "Edit Certificate" : "Add Certificate"} visible={modal.visible} onCancel={closeModal} footer={null} width={720} destroyOnClose>
        <Form form={form} layout="vertical" onFinish={save}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="type_id" label="Certificate Type" rules={[{ required: true, message: "Please select the certificate type." }]}>
                <Select
                  placeholder="Select certificate"
                  onChange={(value) => pickType(value)}
                  showSearch
                  optionFilterProp="children"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: "8px 0" }} />
                      <div style={{ display: "flex", gap: 8, padding: "0 8px 4px" }}>
                        <Input
                          placeholder="New certificate type"
                          value={newType}
                          maxLength={60}
                          onChange={(e) => setNewType(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                        <Button type="link" icon={<PlusOutlined />} loading={addingType} onClick={addType}>
                          Add
                        </Button>
                      </div>
                    </>
                  )}
                >
                  {types.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {t.type}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="name" label="Name">
                <Input placeholder="Optional" maxLength={45} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="state_id" label="State" extra="Leave empty for all states.">
                <Select mode="multiple" placeholder="Optional" allowClear showSearch optionFilterProp="children" maxTagCount={3}>
                  {states.map((s) => (
                    <Option key={s.state_id} value={s.state_id}>
                      {s.state_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="expiry_date" label="Expiry Date" rules={[{ required: true, message: "Please select the expiry date." }]}>
                <DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="File"
                required={!modal.record}
                extra={
                  <>
                    {uploadHint(UPLOAD_RULES.PDF_OR_IMAGE)}
                    {modal.record && <div>Leave empty to keep the current file.</div>}
                  </>
                }
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>{file ? "Change File" : "Select File"}</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: "center" }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              {modal.record ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default memo(CommonCertificates);
